(() => {
  'use strict';

  const namespaceKey = 'GuruShopUtilityTicker';
  const markerSelector = 'script[data-guru-utility-ticker-root]';
  const sourceScript = document.currentScript;
  const requestedRootId = sourceScript?.dataset.guruUtilityTickerRoot;
  const existingApi = window[namespaceKey];

  if (existingApi) {
    existingApi.initById(requestedRootId);
    return;
  }

  const initializedRoots = new WeakSet();
  const cleanupByRoot = new WeakMap();
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  const makeVisualClone = (source) => {
    const clone = source.cloneNode(true);
    clone.removeAttribute('data-guru-ticker-source');
    clone.dataset.guruTickerClone = '';
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');

    clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
    clone.querySelectorAll('[data-shopify-editor-block]').forEach((node) => {
      node.removeAttribute('data-shopify-editor-block');
    });
    clone
      .querySelectorAll('a[href], button, input, select, textarea, [tabindex]')
      .forEach((node) => node.setAttribute('tabindex', '-1'));

    return clone;
  };

  const initRoot = (sectionRoot) => {
    if (!(sectionRoot instanceof HTMLElement) || initializedRoots.has(sectionRoot)) return false;

    const ticker = sectionRoot.querySelector('[data-guru-utility-ticker]');
    const viewport = ticker?.querySelector('[data-guru-ticker-viewport]');
    const track = ticker?.querySelector('[data-guru-ticker-track]');
    const source = ticker?.querySelector('[data-guru-ticker-source]');

    if (!(ticker instanceof HTMLElement)
      || !(viewport instanceof HTMLElement)
      || !(track instanceof HTMLElement)
      || !(source instanceof HTMLElement)) {
      return false;
    }

    const abortController = new AbortController();
    const listenerOptions = { signal: abortController.signal };
    let animationFrame = null;
    let resizeObserver = null;
    let touchReleaseTimer = null;
    let touchManual = false;

    const removeVisualClones = () => {
      track.querySelectorAll('[data-guru-ticker-clone]').forEach((clone) => clone.remove());
    };

    const setManualMode = (active) => {
      ticker.classList.toggle('is-manual', active);
      if (!active) viewport.scrollLeft = 0;
    };

    const releaseTouchManualMode = () => {
      window.clearTimeout(touchReleaseTimer);
      touchReleaseTimer = window.setTimeout(() => {
        touchManual = false;
        if (!source.contains(document.activeElement)) setManualMode(false);
      }, 1600);
    };

    const rebuild = () => {
      animationFrame = null;
      track.classList.remove('is-running');
      removeVisualClones();
      track.style.removeProperty('--guru-utility-ticker-distance');
      ticker.removeAttribute('data-ticker-ready');

      if (reducedMotion.matches) {
        ticker.dataset.tickerReady = 'static';
        return;
      }

      const sourceWidth = source.getBoundingClientRect().width;
      const viewportWidth = viewport.getBoundingClientRect().width;

      if (sourceWidth <= 0 || viewportWidth <= 0) return;

      const cloneCount = Math.max(1, Math.ceil(viewportWidth / sourceWidth));
      const fragment = document.createDocumentFragment();

      for (let index = 0; index < cloneCount; index += 1) {
        fragment.append(makeVisualClone(source));
      }

      track.append(fragment);
      viewport.scrollLeft = 0;
      track.style.setProperty('--guru-utility-ticker-distance', `${sourceWidth}px`);
      void track.offsetWidth;
      track.classList.add('is-running');
      ticker.dataset.tickerReady = 'infinite';
    };

    const queueRebuild = () => {
      if (!initializedRoots.has(sectionRoot)) return;
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
      animationFrame = window.requestAnimationFrame(rebuild);
    };

    const onFocusIn = (event) => {
      const item = event.target instanceof Element
        ? event.target.closest('.guru-utility-ticker__item')
        : null;
      if (!item || !source.contains(item)) return;

      setManualMode(true);
      window.requestAnimationFrame(() => {
        item.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      });
    };

    const onFocusOut = (event) => {
      if (event.relatedTarget instanceof Node && source.contains(event.relatedTarget)) return;
      if (!touchManual) setManualMode(false);
    };

    const onPointerDown = (event) => {
      if (event.pointerType === 'mouse') return;
      touchManual = true;
      setManualMode(true);
      window.clearTimeout(touchReleaseTimer);
    };

    const onPointerEnd = (event) => {
      if (event.pointerType === 'mouse' || !touchManual) return;
      releaseTouchManualMode();
    };

    source.addEventListener('focusin', onFocusIn, listenerOptions);
    source.addEventListener('focusout', onFocusOut, listenerOptions);
    viewport.addEventListener('pointerdown', onPointerDown, listenerOptions);
    viewport.addEventListener('pointerup', onPointerEnd, listenerOptions);
    viewport.addEventListener('pointercancel', onPointerEnd, listenerOptions);
    viewport.addEventListener('scroll', () => {
      if (touchManual) releaseTouchManualMode();
    }, { ...listenerOptions, passive: true });

    if ('ResizeObserver' in window) {
      resizeObserver = new ResizeObserver(queueRebuild);
      resizeObserver.observe(viewport);
      resizeObserver.observe(source);
    } else {
      window.addEventListener('resize', queueRebuild, { ...listenerOptions, passive: true });
    }

    if (typeof reducedMotion.addEventListener === 'function') {
      reducedMotion.addEventListener('change', queueRebuild, listenerOptions);
    } else {
      reducedMotion.addListener(queueRebuild);
    }

    ticker.dataset.tickerBound = 'true';
    initializedRoots.add(sectionRoot);

    cleanupByRoot.set(sectionRoot, () => {
      abortController.abort();
      resizeObserver?.disconnect();
      if (typeof reducedMotion.removeEventListener !== 'function') {
        reducedMotion.removeListener(queueRebuild);
      }
      if (animationFrame !== null) window.cancelAnimationFrame(animationFrame);
      window.clearTimeout(touchReleaseTimer);
      removeVisualClones();
      track.classList.remove('is-running');
      track.style.removeProperty('--guru-utility-ticker-distance');
      ticker.classList.remove('is-manual');
      ticker.removeAttribute('data-ticker-ready');
      delete ticker.dataset.tickerBound;
    });

    document.fonts?.ready.then(queueRebuild);
    queueRebuild();
    return true;
  };

  const cleanupRoot = (sectionRoot) => {
    if (!(sectionRoot instanceof HTMLElement) || !initializedRoots.has(sectionRoot)) return false;

    cleanupByRoot.get(sectionRoot)?.();
    cleanupByRoot.delete(sectionRoot);
    initializedRoots.delete(sectionRoot);
    return true;
  };

  const initById = (rootId) => {
    if (!rootId) return false;
    return initRoot(document.getElementById(rootId));
  };

  const initWithin = (scope) => {
    if (!(scope instanceof Element || scope instanceof Document)) return;

    if (scope.matches?.(markerSelector)) {
      initById(scope.dataset.guruUtilityTickerRoot);
    }

    scope.querySelectorAll(markerSelector).forEach((marker) => {
      initById(marker.dataset.guruUtilityTickerRoot);
    });
  };

  window[namespaceKey] = { cleanupRoot, initById, initRoot, initWithin };

  document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
  document.addEventListener('shopify:section:unload', (event) => cleanupRoot(event.target));

  initById(requestedRootId);
})();
